<script setup lang="ts">
import type { ISpecialProjectYearItemDataClientDTO } from '~/shared/api/client/special_project_year/special_project_year.dto';
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import { resizeImageSetLayers } from '~/shared/lib/resizeImage';
import { SPECIAL_ITEM_EXPERT_IMAGE_WIDTH } from '../lib/const';

const props = defineProps<
  ISpecialProjectYearItemDataClientDTO & {
    textColor: string;
    colSpan?: number;
    utm?: string;
  }
>();

const isExpert = computed(
  () => props.expert !== null && props.expert !== undefined,
);

const isRedirect = computed(() =>
  Boolean(props.news?.redirect && props.news.redirect.length > 0),
);

const person = computed(
  () => props.news?.persons?.[0] ?? props.expert?.person ?? null,
);

const link = computed(() => {
  if (isRedirect.value && props.news?.redirect)
    return props.news.redirect + (props?.utm ?? '');
  if (isExpert.value)
    return `/expert/${getMaterialPublicId(props.expert!)}${props?.utm ?? ''}`;
  return `/${props.news?.type}/${props.news?.id}${props?.utm ?? ''}`;
});
/* Аватар — background-image, srcset тут не работает. */
const expertImageStyle = computed(() => {
  const layers = resizeImageSetLayers(person.value?.photo?.file_url, {
    width: SPECIAL_ITEM_EXPERT_IMAGE_WIDTH,
    height: SPECIAL_ITEM_EXPERT_IMAGE_WIDTH,
    mode: 'fill',
    densities: [1, 2, 3],
  });

  /*
    Массив значений для одного свойства типы Vue не принимают, поэтому
    собираем строку: браузер разберёт объявления по порядку и оставит
    последнее, которое понял.
  */
  return layers.map((layer) => `background-image: ${layer}`).join('; ');
});
</script>

<template>
  <NuxtLink
    :to="link"
    :class="[$style.item, { [$style.item_wide!]: props.colSpan === 2 }]"
    :style="{
      '--text-color': textColor,
      '--person-image': person?.photo?.file_url,
    }"
  >
    <div v-if="person" :class="$style.item__expert">
      <div :class="$style.item__expertImage" :style="expertImageStyle" />
      <div :class="$style.item__expertInfo">
        <div :class="$style.item__expertName">{{ person?.fio }}</div>
        <div :class="$style.item__expertCompany">
          {{ person?.company?.name }}
        </div>
      </div>
    </div>

    <div :class="$style.item__title" :to="link">
      {{ props.title || props.news?.title || props.expert?.title }}
    </div>
  </NuxtLink>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-direction: column;
  gap: var(--space-2-5x);
  height: 100%;

  &__title {
    display: block;
    @include text_body1_medium;
    color: var(--text-color);
    text-decoration: none;
  }

  &__image {
    width: 320;
    height: auto;

    &Wrapper {
      display: flex;
      position: relative;
      width: 320px;
      height: 180px;
      object-fit: cover;
    }
  }

  &__expert {
    display: flex;
    flex-flow: row nowrap;
    gap: var(--space-2-5x);

    &Image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      flex-shrink: 0;
      background-size: cover;
    }

    &Info {
      display: flex;
      flex-flow: column nowrap;
      gap: var(--space-0-5x);
      align-self: center;
    }

    &Name {
      @include text_body1_medium;
      color: var(--text-color);
    }

    &Company {
      @include text_body2;
      color: var(--color-gray-text);
    }
  }

  &_wide {
    flex-direction: row;
    gap: var(--space-5x);
  }
  &_wide &__title {
    flex-grow: 1;
    width: 50%;
  }
  &_wide &__expert {
    flex-grow: 1;
    width: 50%;
    align-items: center;
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .item {
    gap: var(--space-1-5x);
  }
}
</style>
